import React, { useRef, useState, useEffect } from "react";
import { Button, LinearProgress, Stack, Typography } from "@mui/material";
import { SwapHoriz } from "@mui/icons-material";
import { Unity } from "react-unity-webgl";

import PreimageVirtualTourViewerLogo from "components/PreimageVirtualTourViewerLogo";
import useUnityViewer, { UnityViewType } from "hooks/useUnityViewer";
import VirtualTourManualSplashScreen from "./components/Manual";
import ReferenceMeasurementValueInputModal from "./components/ReferenceMeasurementValueInputModal";
import VirtualTourViewerMainToolbar from "./components/VirtualTourViewerMainToolbar";
import UnityViewerVtourModeToolbar from "components/UnityViewerVtourModeToolbar";
import { useAtom, useAtomValue } from "jotai";
import { currentHotspotEventDataAtom } from "store/atoms/CurrentHotspotEventDataAtom";
import { referenceMeasurementInputValueModalStateAtom } from "store/atoms/referenceMeasurementInputValueModalState";
import { newAspectForCompareScanAtom } from "store/atoms/virtualTourViewer/newAspectForCompareScan";
import { hasUnityLoadedOnceAtom } from "store/atoms/unityHasLoadedOnce";
import { isInstructionSplashScreenOpenAtom } from "store/atoms/virtualTourViewer/isInstructionSplashScreenOpen";
import Minimap from "./miniMap";
import PointCloudViewer from "./pointcloud/PointCloudContainer/PointCloudViewer";
import { useGetOutputPointcloud } from "api/output/queries/useGetOutputPointcloud";
import { useGetScanQuery } from "api/scan/queries/useGetScanQuery";
import { useParams, useSearchParams } from "react-router-dom";
import { setPoreeCameraAtom } from "store/atoms/virtualTourViewer/setPotreeCameraAtom";
import { getMiniMapSize } from "utils/potree";
import useCurrentUserProfile from "hooks/useCurrentUserProfile";
import { useLoading } from "contexts/LoadingContext";
import BlurryOverlay from "components/BlurryOverlay";

type UnityViewerProps = {
  scanType: UnityViewType;
  isEnabled?: boolean;
};

export const UnityViewer = ({
  scanType = "uniscan",
  isEnabled = true,
}: UnityViewerProps) => {
  const unityViewerRef = useRef<HTMLDivElement>(null);
  const unityViewerCanvasRef = useRef<HTMLCanvasElement>(null);
  const { unityViewerContext, actions } = useUnityViewer(scanType);
  const [currentHotspotEventData, setCurrentHotspotEventData] = useAtom(
    currentHotspotEventDataAtom
  );
  const { getCurrentHotspotId } = actions;
  const referenceMeasurementInputValueModalState = useAtomValue(
    referenceMeasurementInputValueModalStateAtom
  );
  const { ptcldOutputId1, setBlurryEffect, blurryEffect } = useLoading();

  const [isDragging, setIsDragging] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ x: "50%", y: "50%" });
  const [newAspectForCompareScan, setNewAspectForCompareScan] = useAtom(
    newAspectForCompareScanAtom
  );
  const [hasUnityLoadedOnce, setHasUnityLoadedOnce] = useAtom(
    hasUnityLoadedOnceAtom
  );
  const [isPotreeCameraSetString, setPotreeCameraString] =
    useAtom(setPoreeCameraAtom);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [searchParams] = useSearchParams();

  const { userProfile } = useCurrentUserProfile();

  const shouldInstructionSplashScreen = userProfile?.metadata?.showVirtualTourManual ?? true;

  const {
    data: pointCloudData,
    isLoading: dataLoading,
    error: dataError,
  } = useGetOutputPointcloud(ptcldOutputId1);
  const focusCanvas = () => unityViewerCanvasRef?.current?.focus();
  const shouldLoadPotree =
    Boolean(pointCloudData) &&
    Boolean(
      JSON.parse(isPotreeCameraSetString)?.thumbnail?.center.length > 0
    ) &&
    isEnabled;


  const [isInstructionSplashScreenOpen, setIsInstructionSplashScreenOpen] =
    useAtom(isInstructionSplashScreenOpenAtom);

  useEffect(() => {
    focusCanvas();
  }, []);

  
useEffect(()=>{
  setIsInstructionSplashScreenOpen(shouldInstructionSplashScreen);
  },[shouldInstructionSplashScreen])

  useEffect(() => {
    if (
      unityViewerContext &&
      unityViewerContext.isLoaded &&
      hasUnityLoadedOnce
    ) {
      const timer = setTimeout(() => {
        setIsButtonEnabled(true);
      }, 2100);

      return () => clearTimeout(timer);
    }
  }, [unityViewerContext?.isLoaded, hasUnityLoadedOnce]);

  useEffect(() => {
    if (unityViewerContext && unityViewerContext.isLoaded && blurryEffect) {
      setBlurryEffect(false);
    }
  }, [unityViewerContext?.isLoaded]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging && unityViewerRef.current) {
      const rect = unityViewerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;

      const newX = Math.min(Math.max(x, 15), rect.width - 15);
      setNewAspectForCompareScan(newX / rect.width);
      setButtonPosition({
        x: `${newX}px`,
        y: buttonPosition.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  if (unityViewerContext && unityViewerContext.initialisationError) {
    return (
      <Stack
        flex={1}
        alignItems="center"
        justifyContent="center"
        height="100%"
        position="relative"
      >
        <Typography>
          {unityViewerContext.initialisationError.message}
        </Typography>
      </Stack>
    );
  }

  if (!unityViewerContext) {
    console.error("Unity Viewer Context not found");
    return (
      <Stack
        flex={1}
        alignItems="center"
        justifyContent="center"
        height="100%"
        position="relative"
      >
        <Typography>Error loading Unity Viewer</Typography>
      </Stack>
    );
  }

  return (
    <Stack
      ref={unityViewerRef}
      height="100%"
      flex={3}
      alignItems="center"
      justifyContent="center"
      position="relative"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseUp}
      onMouseUp={handleMouseUp}
    >
      <Unity
        tabIndex={1}
        unityProvider={unityViewerContext.unityProvider}
        ref={unityViewerCanvasRef}
        style={{
          width: "100%",
          height: "100%",
          transition: "all 0.5s",
          background: "#000",
        }}
        devicePixelRatio={window.devicePixelRatio || 1}
      />
      {shouldLoadPotree && (
        <Stack
          sx={{
            height: "0%",
            width: "0%",
            position: "absolute",
            top: "0",
            right: "0px",
            transform: `translateX(${-getMiniMapSize() - 10}px)`,
          }}
        >
          <PointCloudViewer
            pointCloudData={pointCloudData}
            dataError={dataError}
            isUniScan="This is the minimap"
          />
        </Stack>
      )}
      <ReferenceMeasurementValueInputModal />
      {unityViewerContext.isLoaded && <VirtualTourManualSplashScreen />}
      {unityViewerContext.isLoaded && <VirtualTourViewerMainToolbar />}
      {!unityViewerContext.isLoaded && !blurryEffect && (
        <Stack
          spacing={
            scanType === "uniscan-compare"
              ? {
                  xs: 3,
                  sm: 6,
                  md: 10,
                }
              : {
                  xs: 5,
                  sm: 10,
                }
          }
          alignItems="center"
          position="absolute"
        >
          <Stack direction="row" spacing={4} alignItems="center">
            <PreimageVirtualTourViewerLogo
              style={{
                width:
                  scanType === "uniscan-compare"
                    ? {
                        xs: 150,
                        sm: 200,
                        md: 400,
                      }
                    : {
                        xs: 200,
                        sm: 400,
                      },
                height: "100%",
              }}
            />
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.round(unityViewerContext.loadingProgression * 100)}
            sx={{
              height: 10,
              width:
                scanType === "uniscan-compare"
                  ? {
                      xs: "100%",
                    }
                  : {
                      xs: "100%",
                      md: 600,
                    },
              borderRadius: 5,
            }}
          />
        </Stack>
      )}
      {unityViewerContext.isLoaded &&
        referenceMeasurementInputValueModalState === "closed" && (
          <UnityViewerVtourModeToolbar
            shareHotspotHandler={actions.getCurrentHotspotId}
          />
        )}
      {unityViewerContext.isLoaded && hasUnityLoadedOnce && isButtonEnabled && (
        <div
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          style={{
            position: "absolute",
            top: buttonPosition.y,
            left: buttonPosition.x,
            transform: "translate(-50%, -50%)",
            width: "30px",
            height: "30px",
            borderRadius: "50%",
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            color: "black",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isDragging ? "grabbing" : "grab",
            userSelect: "none",
            backdropFilter: "blur(4px)",
          }}
        >
          <SwapHoriz sx={{ fontSize: 25 }} />
        </div>
      )}
      {/* Minimap positioned in the top-right corner */}
      {/* <Minimap
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          zIndex: 10, // Ensures it stays on top
        }}
      /> */}
      <BlurryOverlay loading={blurryEffect} />
    </Stack>
  );
};
